import { defineStore, acceptHMRUpdate } from 'pinia';

export const useContextStore = defineStore('ContextStore', () => {
  if (!useAppRunningStore().isRunning) return;

  useAppEventsExport();

  useSessionStore();

  const profile = useProfileStore();

  const profileLoadFromNetwork = useProfileLoadFromNetwork();

  const user = useUserStore();

  const gaming = useGamingStore();

  const assessments = useAssessmentsStore();

  const config = useConfigStore();

  const adamAuth = useAdamAuth();

  const needRefresh = ref<boolean>(true);

  const hasBeenInitialized = ref<boolean>(false);

  const isLoggedIn = computed(() => adamAuth.isLoggedIn.value);

  const memberId = computed(() => adamAuth.memberId.value);

  const userIdFromAuthCookie = computed(() => adamAuth.userId.value);

  const versions = useVersionsStore();

  const isPdfViewer = () => {
    return window.location.pathname === '/pdf/viewer';
  };

  const initClient = async (loadConfig: boolean) => {
    // is only call from useAppInit
    hasBeenInitialized.value = true;
    if (isLoggedIn.value === true) {
      // cookie is set with memberId and userId
      await loadDataFromClient();
      if (!user.data || !profile.data || !config.data) {
        if (!user.id) {
          await loadDataFromNetwork();
        }
      }
      if (user.id !== userIdFromAuthCookie.value) {
        // something goes wrong
        window.location.href = '/logout';
      }
    } else if (loadConfig) {
      await useConfigLoadFromNetwork();
    }
  };

  const loadDataFromClient = async (): Promise<void> => {
    await Promise.all([
      config.loadFromClient(),
      profile.loadFromClient(),
      user.loadFromClient(),
      assessments.loadFromClient(),
      gaming.loadFromClient(),
    ]);
  };

  const loadDataFromNetwork = async (
    showProgress: boolean = false
  ): Promise<void> => {
    await Promise.all([
      useConfigLoadFromNetwork(),
      useUserLoadFromNetwork(),
      profileLoadFromNetwork.run(true, false, showProgress),
      useGamingLoadFromNetwork(),
    ]);
  };

  const debouncedForceLoadProfileFromNetwork = useDebounceFn(async () => {
    try {
      await profileLoadFromNetwork.run();
    } catch (e) {
      globalThis.$logger.error('not able to load user and profile');
    }
  }, 1000);

  const forceLoadProfileFromNetwork = (debounced = true) => {
    if (debounced) {
      debouncedForceLoadProfileFromNetwork();
    }
  };

  const isFormal = computed(() => {
    if (profile.data) {
      return profile.useFormal;
    } else {
      return true;
    }
  });

  watchEffect(() => {
    if (profile.textDyslexicMode) {
      document.documentElement.classList.add('text-dyslexic-mode');
    } else {
      document.documentElement.classList.remove('text-dyslexic-mode');
    }
  });

  watch(
    () => isLoggedIn.value,
    async (to, from) => {
      if (!to) {
        globalThis.$logger.info('redirect to disconnected');
        if (window.location.href !== '/logout') {
          window.location.href = '/disconnected';
        }
      }
    }
  );

  return {
    memberId,
    userId: userIdFromAuthCookie,
    initClient,
    isLoggedIn,
    loadDataFromClient,
    loadDataFromNetwork,
    user,
    profile,
    config,
    isPdfViewer,
    hasBeenInitialized,
    isFormal,
    needRefresh,
    adamAuth,
    forceLoadProfileFromNetwork,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useContextStore, import.meta.hot));
}
